import { Stack, Typography, Box, Divider, useTheme } from "@mui/material";
import React from "react";
import { CollapsableCard } from "./collapsable_card";
import { useQuery } from "@tanstack/react-query";
import { REACT_QUERY_KEYS } from "../../enums/react_query_key_enum";
import { getOptimizationParameters } from "../../scripts/api_v2/parameters_client";
import useBackend from "../../hooks/use_backend";
import { useParams } from "react-router-dom";
import Loader from "./loader";

const ParameterRow = ({ label, value }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
    <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 700, color: "text.secondary" }}>
      {value}
    </Typography>
  </Box>
);

export default function OptimizationParameters({
  tooltip,
  ...props
}) {
  const theme = useTheme();
  const backend = useBackend();
  const params = useParams();

  const {
    data: optimizationData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [REACT_QUERY_KEYS.OPTIMIZATION_PARAMETERS, backend.fetch, params.site_id],
    queryFn: () => getOptimizationParameters(backend.fetch, params.site_id),
    enabled: !!params.site_id,
  });

  const getTaxesDisplay = () => {
    if (!optimizationData?.taxes) return "—";
    const { offtake_mwh, injection_mwh } = optimizationData.taxes;
    return `${offtake_mwh} / ${injection_mwh} €/MWh`;
  };

  const getMarginsDisplay = () => {
    return optimizationData?.margins ?? "—";
  };

  if (isLoading) {
    return (
      <CollapsableCard title="Optimization Parameters" tooltip={tooltip} {...props}>
        <Loader />
      </CollapsableCard>
    );
  }

  if (isError) {
    return (
      <CollapsableCard title="Optimization Parameters" tooltip={tooltip} {...props}>
        <Typography variant="body2" sx={{ color: "text.primary", fontStyle: "italic" }}>
          Error loading optimization parameters
        </Typography>
      </CollapsableCard>
    );
  }
  return (
    <CollapsableCard
      title="Optimization Parameters"
      tooltip={tooltip}
      {...props}
    >
      <Stack direction="column" spacing={1} sx={{ mt: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary", fontSize: "0.875rem" }}>
          The following parameters are considered during optimization
        </Typography>

        <Divider />

        <ParameterRow
          label="Taxes offtake / injection"
          value={getTaxesDisplay()}
        />

        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: "text.primary" }}>
            GPCs
          </Typography>
          {optimizationData?.gpcs && optimizationData.gpcs.length > 0 ? (
            <Stack direction="column" spacing={1.5} sx={{
              pl: 2,
              borderLeft: `3px solid ${theme.palette.divider}`
            }}>
              {optimizationData.gpcs.map((gpc) => (
                <Box key={gpc.device_id} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }}>
                    {gpc.device_id}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "text.secondary", whiteSpace: "nowrap" }}>
                    {gpc.price} €/MWh
                  </Typography>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" sx={{ color: "text.primary", fontStyle: "italic" }}>
              No GPC data available
            </Typography>
          )}
        </Box>

        <Divider />

        <ParameterRow
          label="Margins"
          value={getMarginsDisplay()}
        />
      </Stack>
    </CollapsableCard>
  );
}
